import { useState } from 'react'
import { X, FileText, FileCheck, FileClock, Download, Loader2 } from 'lucide-react'
import { pepApi } from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'

/**
 * PepExportModal — Modal per esportare il PDF del PEP
 *
 * Scelta tra 3 varianti, sintesi valutativa (completo), download blob.
 *
 * Props:
 *   pepId       - ObjectId del PEP
 *   pepStato    - 'attivo' | 'chiuso' (per abilitare variante chiusura)
 *   onClose     - callback chiusura
 */

const VARIANTS = [
  {
    key: 'completo',
    label: 'Relazione completa',
    desc: 'Documento formale con tutti i dettagli: anagrafica, contesto, obiettivi con verifiche, statistiche, colloqui.',
    icon: FileText,
    color: 'border-primary-300 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-700'
  },
  {
    key: 'sintesi',
    label: 'Sintesi',
    desc: 'Versione compatta: dati essenziali, tabella obiettivi, indicatori chiave. Ideale per comunicazioni rapide.',
    icon: FileCheck,
    color: 'border-green-300 bg-green-50 dark:bg-green-900/20 dark:border-green-700'
  },
  {
    key: 'chiusura',
    label: 'Relazione di chiusura',
    desc: 'Report di fine percorso con confronto inizio/fine, esiti obiettivi, sintesi finale e raccomandazioni.',
    icon: FileClock,
    color: 'border-orange-300 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700',
    requiresChiuso: true
  }
]

export default function PepExportModal({ pepId, pepStato, onClose }) {
  const { showError } = useNotification()
  const [selected, setSelected] = useState('completo')
  const [sintesiValutativa, setSintesiValutativa] = useState('')
  const [generating, setGenerating] = useState(false)

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const payload = { variant: selected }
      if (selected === 'completo' && sintesiValutativa.trim()) {
        payload.sintesiValutativa = sintesiValutativa.trim()
      }

      const response = await pepApi.exportPdf(pepId, payload)

      // Download blob
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      // Extract filename from Content-Disposition or fallback
      const cd = response.headers?.['content-disposition'] || ''
      const filenameMatch = cd.match(/filename="?([^"]+)"?/)
      link.download = filenameMatch?.[1] || `PEP_${selected}_${new Date().toISOString().slice(0, 10)}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)

      onClose()
    } catch (error) {
      // Se il blob è un JSON di errore, prova a parsarlo
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const json = JSON.parse(text)
          showError(json.message || 'Errore nella generazione del PDF')
        } catch {
          showError('Errore nella generazione del PDF')
        }
      } else {
        showError(error.response?.data?.message || 'Errore nella generazione del PDF')
      }
    } finally {
      setGenerating(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white dark:bg-gray-800 w-full max-w-lg mx-4 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Download className="h-5 w-5 text-primary-500" />
            Esporta PDF
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Variant selection */}
          <div className="space-y-3">
            {VARIANTS.map(v => {
              const Icon = v.icon
              const disabled = v.requiresChiuso && pepStato !== 'chiuso'

              return (
                <button
                  key={v.key}
                  onClick={() => !disabled && setSelected(v.key)}
                  disabled={disabled}
                  className={`w-full text-left p-4 border-2 rounded-lg transition-all ${
                    disabled
                      ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700'
                      : selected === v.key
                        ? `${v.color} ring-2 ring-current`
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <Icon className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                      selected === v.key ? 'text-current' : 'text-gray-400'
                    }`} />
                    <div>
                      <p className="font-medium text-sm text-gray-900 dark:text-white">
                        {v.label}
                        {disabled && <span className="ml-2 text-xs text-gray-400">(solo PEP chiusi)</span>}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{v.desc}</p>
                    </div>
                  </div>
                </button>
              )
            })}
          </div>

          {/* Sintesi valutativa — solo per variante completo */}
          {selected === 'completo' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sintesi valutativa
                <span className="text-xs font-normal text-gray-400 ml-1">(opzionale)</span>
              </label>
              <textarea
                value={sintesiValutativa}
                onChange={(e) => setSintesiValutativa(e.target.value)}
                maxLength={5000}
                rows={4}
                className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                placeholder="Valutazione complessiva del percorso educativo... (verrà inclusa nell'ultima sezione del PDF)"
              />
              <p className="text-xs text-gray-400 text-right mt-1">{sintesiValutativa.length}/5000</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            disabled={generating}
            className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 px-3 py-2"
          >
            Annulla
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-2 text-sm font-medium bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Genera PDF
          </button>
        </div>
      </div>
    </div>
  )
}
